import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SKETCH_URL } from '../app-urls';
import { CURRENT_STATE_PROJECT_KEY, MODEL_TYPE, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { Organisation } from '../common/organisation';
import { MODEL_API, ORGANISATION_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsRefreshService } from '../model-services/insights-refresh.service';
import { ModelService } from '../organisation/models/model-service';
import { canCorrect, correctionChanges, NodeCorrection } from './node-correction';

export type { NodeCorrection } from './node-correction';

const COMPONENT = 'Node correction';

// Lets someone looking into friction, risk or the value of information put right a node the finding rests on: open it
// in its model to edit it there, or change the one property behind the finding from the view itself, which then has the
// insights worked out again
@Injectable({ providedIn: 'root' })
export class NodeCorrectionService {
    constructor(private repoService: RepositoryService, private refreshes: InsightsRefreshService, private modelService: ModelService,
        private router: Router) {}

    canCorrect(organisation: Organisation | undefined, workspaceKey: string) {
        return canCorrect(organisation, workspaceKey);
    }

    // opens the node's model with the node selected, so its properties can be edited; a role or system in the
    // organisation's people and systems. Without access to the model, offers to ask for it
    openInModel(organisation: Organisation, modelKey: string, nodeKey: string, workspaceKey = CURRENT_STATE_PROJECT_KEY) {
        if (modelKey === ORGANISATION_MODEL_KEY) {
            void this.router.navigate([SKETCH_URL(organisation._key, ORGANISATION_MODEL_KEY, MODEL_TYPE.PEOPLE_AND_SYSTEMS, workspaceKey)], { queryParams: { nodeKey } });
            return;
        }
        const model = organisation.models?.find(m => m._key === modelKey);
        if (model) this.modelService.editModel(organisation, model, workspaceKey, nodeKey);
        else void this.router.navigate([SKETCH_URL(organisation._key, modelKey, MODEL_TYPE.PROCESS, workspaceKey)], { queryParams: { nodeKey } });
    }

    // saves the changes in their current state models, then has the organisation's insights worked out again. Throws
    // if either fails, so the view can say so
    async apply(organisation: Organisation, corrections: NodeCorrection[], what: string) {
        if (!corrections.length) return;
        const changes = correctionChanges(corrections);
        for (const modelKey of Object.keys(changes)) {
            await this.repoService.callSingleAPI(this.repoService.update(MODEL_API(organisation._key, modelKey, CURRENT_STATE_PROJECT_KEY), changes[modelKey]), COMPONENT, what);
        }
        await this.refreshes.track(this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(organisation._key, CURRENT_STATE_PROJECT_KEY)}/refresh`, {}), COMPONENT, 'Rescore the models'), CURRENT_STATE_PROJECT_KEY);
    }
}
